import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Award, 
  Clock, 
  Shield, 
  ThumbsUp, 
  Building2, 
  Factory, 
  Home, 
  Hammer, 
  Sparkles, 
  SprayCan,
  ArrowRight,
  CheckCircle,
  Leaf,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useLanguage();

  const heroImages = useMemo(() => [
    { id: 'hero-1', url: "https://images.unsplash.com/photo-1627905646269-7f034dcc5738", alt: "Nettoyage de bureaux professionnel" },
    { id: 'hero-2', url: "https://images.pexels.com/photos/34516670/pexels-photo-34516670.jpeg", alt: "Agent d'entretien dans un bureau moderne" },
    { id: 'hero-3', url: "https://images.unsplash.com/photo-1437326300822-01d8f13c024f", alt: "Nettoyage de sols" },
    { id: 'hero-4', url: "https://images.unsplash.com/photo-1669101602124-f5b78895d91c", alt: "Équipe de nettoyage commercial en uniforme" }
  ], []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  const nextSlide = useCallback(() => setCurrentSlide((prev) => (prev + 1) % heroImages.length), [heroImages.length]);
  const prevSlide = useCallback(() => setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length), [heroImages.length]);

  const values = [
    { icon: Award, title: t('expertise'), description: t('expertiseDesc') },
    { icon: Clock, title: t('rapidite'), description: t('rapiditeDesc') },
    { icon: Shield, title: t('qualite'), description: t('qualiteDesc') },
    { icon: ThumbsUp, title: t('confiance'), description: t('confianceDesc') },
  ];

  const services = [
    { icon: Building2, title: t('bureauxLocaux'), description: t('bureauxLocauxDesc'), color: "bg-blue-50 text-[#1E3A8A]" },
    { icon: Factory, title: t('nettoyageIndustrielTitle'), description: t('nettoyageIndustrielDesc'), color: "bg-slate-100 text-slate-700" },
    { icon: Home, title: t('immeublesCopro'), description: t('immeublesCoproDesc'), color: "bg-green-50 text-[#10B981]" },
    { icon: Hammer, title: t('apresTravaux'), description: t('apresTravauxDesc'), color: "bg-amber-50 text-amber-600" },
    { icon: Sparkles, title: t('vitresSurfacesTitle'), description: t('vitresSurfacesDesc'), color: "bg-sky-50 text-sky-600" },
    { icon: SprayCan, title: t('desinfectionTitle'), description: t('desinfectionDesc'), color: "bg-emerald-50 text-emerald-600" },
  ];

  const stats = [
    { value: "15+", label: t('anneesExperience') },
    { value: "200", label: t('clientsSatisfaits') },
    { value: "✓", label: t('reactifs') },
    { value: "100%", label: t('engagementRseLabel') },
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      <SEO
        title="Services de nettoyage professionnel en Belgique"
        description="Nettis Facility, votre partenaire pour le nettoyage professionnel en Belgique. Bureaux, industries, copropriétés. Devis gratuit, équipes formées, produits écologiques. Bruxelles et toute la Belgique."
        keywords="nettoyage professionnel Belgique, nettoyage bureaux Bruxelles, société nettoyage, entretien locaux professionnels, nettoyage industriel, Nettis Facility"
        canonicalUrl="https://www.nettis.be"
      />

      {/* Hero Section with Carousel */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          {heroImages.map((image) => (
            <div
              key={image.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${heroImages.indexOf(image) === currentSlide ? "opacity-100" : "opacity-0"}`}
              style={{ backgroundImage: `url(${image.url})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/50"></div>
        </div>

        <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors">
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroImages.map((image, index) => (
            <button
              key={image.id}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? "bg-[#10B981] w-8" : "bg-white/50 hover:bg-white/80"}`}
            />
          ))}
        </div>
        
        <div className="relative container mx-auto px-4 md:px-6 max-w-7xl py-20 z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
              {t('garantitEnvironnement')} <span className="text-[#10B981]">{t('propre')}</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed">
              {t('heroDescription')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-green-900/30 transition-transform duration-200 hover:-translate-y-1">
                  {t('nousContacter')}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium">
                  {t('decouvrirServices')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={value.title} className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-14 h-14 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center mb-4 group-hover:bg-[#1E3A8A] transition-colors duration-300">
                  <value.icon className="w-7 h-7 text-[#1E3A8A] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">{value.title}</h3>
                <p className="text-sm text-slate-500">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">{t('nosExpertises')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">{t('servicesNettoyagePro')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('servicesDescription')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={service.title} className="group bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className={`w-16 h-16 rounded-xl ${service.color} flex items-center justify-center mb-6`}>
                  <service.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <Link to="/services" className="inline-flex items-center text-[#1E3A8A] font-medium hover:text-[#2563EB] transition-colors">
                  {t('enSavoirPlus')}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-[#1E3A8A] hover:bg-[#2563EB] text-white rounded-full px-8 py-3 font-semibold shadow-lg shadow-blue-900/20 transition-transform duration-200 hover:-translate-y-0.5">
                {t('voirTousServices')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* RSE Highlight Section */}
      <section data-testid="rse-highlight" className="py-24 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">{t('engagementEcologique')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                {t('acteurEcoResponsable')} <span className="text-[#10B981]">{t('ecoResponsable')}</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">{t('rseDescription')}</p>
              
              <div className="space-y-4 mb-8">
                {[t('rsePoint1'), t('rsePoint2'), t('rsePoint3'), t('rsePoint4')].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#10B981] shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/engagement-rse">
                <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-6 py-3 font-semibold shadow-lg shadow-green-900/20 transition-transform duration-200 hover:-translate-y-0.5">
                  {t('decouvrirEngagement')}
                  <Leaf className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1731119415638-a19158e0fef0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxlY28lMjBmcmllbmRseSUyMGNsZWFuaW5nJTIwbmF0dXJlJTIwZ3JlZW4lMjBsZWFmJTIwc29hcHxlbnwwfHx8fDE3NzIxMzc4Mzd8MA&ixlib=rb-4.1.0&q=85"
                alt="Engagement écologique"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi Nous Choisir Section */}
      <section data-testid="why-us-section" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">Nos avantages</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
              Pourquoi choisir <span className="text-[#1E3A8A]">Nettis Facility</span> ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Plus qu'une société de nettoyage, un véritable partenaire pour l'entretien de vos locaux professionnels en Belgique.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Équipes qualifiées et assurées",
                description: "Personnel formé aux techniques professionnelles, vérifié et couvert par notre assurance RC Pro.",
                highlight: false
              },
              {
                icon: Award,
                title: "Qualité garantie",
                description: "Contrôles réguliers, cahiers de liaison et engagement de résultat. Satisfaction client garantie.",
                highlight: true
              },
              {
                icon: Clock,
                title: "Flexibilité totale",
                description: "Interventions en journée, soirée ou week-end selon vos besoins. Contrats adaptés à votre activité.",
                highlight: false
              },
              {
                icon: Leaf,
                title: "Produits écologiques",
                description: "Engagement RSE fort avec des produits certifiés Ecolabel, respectueux de l'environnement.",
                highlight: false
              },
              {
                icon: Building2,
                title: "Couverture nationale",
                description: "Présents à Bruxelles, en Wallonie et en Brabant. Des équipes locales proches de vous.",
                highlight: false
              },
              {
                icon: ThumbsUp,
                title: "Devis gratuit sous 24h",
                description: "Étude personnalisée de vos besoins et proposition tarifaire sans engagement.",
                highlight: false
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className={`p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                  item.highlight 
                    ? 'bg-gradient-to-br from-[#1E3A8A] to-[#0F172A] text-white shadow-xl' 
                    : 'bg-slate-50 hover:shadow-lg'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                  item.highlight ? 'bg-white/10' : 'bg-[#1E3A8A]/10'
                }`}>
                  <item.icon className={`w-7 h-7 ${item.highlight ? 'text-[#10B981]' : 'text-[#1E3A8A]'}`} />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${item.highlight ? 'text-white' : 'text-[#0F172A]'}`}>
                  {item.title}
                </h3>
                <p className={item.highlight ? 'text-blue-100' : 'text-slate-600'}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3 font-semibold shadow-lg shadow-green-900/20">
                Demander un devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={stat.label} className="text-center">
                <p className="text-4xl md:text-5xl font-extrabold text-white mb-2">
                  <span className="bg-gradient-to-r from-[#10B981] to-[#2563EB] bg-clip-text text-transparent">{stat.value}</span>
                </p>
                <p className="text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="bg-gradient-to-br from-[#1E3A8A] to-[#172554] rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-[#10B981] rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('pretCollaborer')}</h2>
              <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">{t('ctaDescription')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg transition-transform duration-200 hover:-translate-y-1">
                    {t('nousContacter')}
                  </Button>
                </Link>
                <Link to="/postuler#formulaire">
                  <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium">
                    {t('rejoindreEquipe')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
